import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const [submitStatus, setSubmitStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);
    try {
      const response = await fetch("/send_mail.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (result.success) {
        setSubmitStatus({ success: true, message: "Message sent successfully!" });
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        setSubmitStatus({ success: false, message: result.message || "Failed to send message." });
      }
    } catch (error) {
      setSubmitStatus({ success: false, message: "An error occurred. Please try again later." });
    }
    setLoading(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Our Location',
      content: 'Techno Hub – 2, Dubai Silicon Oasis',
      subcontent: 'Dubai, UAE'
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '+971 54 296 9990',
      link: 'tel:+971542969990'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'sales@turbowingaviation.com',
      link: 'mailto:sales@turbowingaviation.com'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Sunday - Thursday',
      subcontent: '9:00 AM - 6:00 PM GST'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#1a2642] to-[#0a1628] pt-24 sm:pt-28 pb-12 sm:pb-20 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2Q0YWYzNyIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 space-y-4">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white">
            Get In <span className="text-[#d4af37]">Touch</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to elevate your aviation supply chain? Contact us today for expert assistance.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Contact Form */}
          <Card className="shadow-2xl border border-[#d4af37]/20 bg-gradient-to-br from-[#1a2642] to-[#0a1628]">
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                                {submitStatus && (
                                  <div className={`text-center font-semibold rounded-lg py-3 px-4 mb-2 ${submitStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {submitStatus.message}
                                  </div>
                                )}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#0a1628] border border-[#d4af37]/30 text-white rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent transition-all duration-300 placeholder-gray-500"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#0a1628] border border-[#d4af37]/30 text-white rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent transition-all duration-300 placeholder-gray-500"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#0a1628] border border-[#d4af37]/30 text-white rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent transition-all duration-300 placeholder-gray-500"
                    placeholder="+971 XX XXX XXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#0a1628] border border-[#d4af37]/30 text-white rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent transition-all duration-300 placeholder-gray-500"
                    placeholder="Your Company"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-3 bg-[#0a1628] border border-[#d4af37]/30 text-white rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent transition-all duration-300 resize-none placeholder-gray-500"
                    placeholder="Tell us about your requirements..."
                  ></textarea>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#d4af37] to-[#c9a869] hover:from-[#f4e4c1] hover:to-[#d4af37] text-[#0a1628] font-bold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-2xl hover:shadow-[#d4af37]/50 group disabled:opacity-60"
                  disabled={loading}
                >
                  <span className="flex items-center justify-center">
                    {loading ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              Contact Information
            </h2>

            <div className="space-y-4">
              {contactInfo.map((info, index) => (
                <Card 
                  key={index}
                  className="group hover:shadow-xl hover:shadow-[#d4af37]/20 transition-all duration-300 hover:-translate-y-1 border border-[#d4af37]/20 bg-gradient-to-br from-[#1a2642] to-[#0a1628]"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#d4af37] to-[#c9a869] p-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                          <info.icon className="w-full h-full text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-1">
                          {info.title}
                        </h3>
                        {info.link ? (
                          <a 
                            href={info.link}
                            className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300 block"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-gray-300">{info.content}</p>
                        )}
                        {info.subcontent && (
                          <p className="text-gray-400 text-sm mt-1">{info.subcontent}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Why Contact Us */}
            <Card className="bg-gradient-to-br from-[#0a1628] to-[#1e3a8a] text-white border-none mt-8">
              <CardContent className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 flex items-center">
                  <Globe className="w-6 h-6 mr-2 text-[#d4af37]" />
                  Why Choose Us?
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#d4af37] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>24/7 customer support</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#d4af37] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Fast response time (within 24 hours)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#d4af37] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Expert aviation supply chain consultants</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#d4af37] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Global reach, serving 100+ countries</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
